import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialiser avec la préférence système ou localStorage
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('autoedito_theme');
    if (stored) return stored;
    
    // Détecter la préférence système
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    return 'dark';
  });

  useEffect(() => {
    // Sauvegarder dans localStorage
    localStorage.setItem('autoedito_theme', theme);
    
    // Appliquer la classe au document
    const root = document.documentElement;
    const body = document.body;
    
    if (theme === 'light') {
      root.classList.add('light-theme');
      root.classList.remove('dark-theme');
      // Appliquer les styles directement au body
      body.style.backgroundColor = '#f1f5f9';
      body.style.color = '#0f172a';
    } else {
      root.classList.add('dark-theme');
      root.classList.remove('light-theme');
      body.style.backgroundColor = '#020617';
      body.style.color = '#f1f5f9';
    }
  }, [theme]);

  // Écouter les changements de préférence système
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const handleChange = (e) => {
      const stored = localStorage.getItem('autoedito_theme');
      // Ne changer que si l'utilisateur n'a pas fait de choix explicite
      if (!stored) {
        setTheme(e.matches ? 'light' : 'dark');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const isDark = theme === 'dark';
  
  // Couleurs pour les composants
  const colors = isDark ? {
    bgPrimary: '#020617',
    bgSecondary: '#0f172a',
    bgCard: 'rgba(15, 23, 42, 0.4)',
    borderPrimary: 'rgba(51, 65, 85, 0.6)',
    textPrimary: '#f1f5f9',
    textSecondary: '#94a3b8',
    textMuted: '#64748b',
  } : {
    bgPrimary: '#f1f5f9',
    bgSecondary: '#ffffff',
    bgCard: 'rgba(255, 255, 255, 0.9)',
    borderPrimary: '#cbd5e1',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
